/**
 * TWA (Trusted Web Activity) detection utility.
 *
 * Detecta si la app está corriendo dentro del APK Android generado por Bubblewrap.
 * Métodos combinados (por robustez cross-browser):
 *
 * 1. document.referrer inicia con "android-app://" (Chrome Custom Tabs)
 * 2. URLSearchParams contiene `?source=twa` o `?source=android`
 *    (pasado como startUrl en twa-manifest.json)
 * 3. localStorage cache "twa_client_mode" (persistente entre sesiones)
 * 4. media query display-mode: standalone (PWA instalada — no exclusivo de TWA)
 *
 * Regla de negocio: si es TWA, solo usuarios con rol ASEGURADO pueden autenticarse.
 * ADMIN / SUPERADMIN / OPERADOR / SUPERVISOR reciben mensaje y forzado logout.
 */

const STORAGE_KEY = 'twa_client_mode';
const ALLOWED_ROLE = 'ASEGURADO';

/**
 * Verifica si el contexto actual es un TWA (APK Android).
 * @returns {boolean}
 */
export const isTWAContext = () => {
  if (typeof window === 'undefined') return false;

  // 1. Persistent flag (once detected, we remember it for the session/browser)
  try {
    if (window.localStorage.getItem(STORAGE_KEY) === '1') return true;
  } catch (_) { /* private mode SecurityError */ void 0; }

  // 2. URL query param (used as canonical start URL in twa-manifest.json)
  const params = new URLSearchParams(window.location.search);
  const source = (params.get('source') || '').toLowerCase();
  if (source === 'twa' || source === 'android' || source === 'apk') {
    try { window.localStorage.setItem(STORAGE_KEY, '1'); } catch (_) { void 0; }
    return true;
  }

  // 3. Referrer emitted by Chrome Custom Tabs / TWA
  if (typeof document !== 'undefined' && document.referrer) {
    if (document.referrer.startsWith('android-app://')) {
      try { window.localStorage.setItem(STORAGE_KEY, '1'); } catch (_) { void 0; }
      return true;
    }
  }

  return false;
};

/**
 * Verifica si un rol es aceptado dentro del contexto TWA.
 * @param {string} role - Nombre del rol (ASEGURADO, ADMIN, ...)
 * @returns {boolean}
 */
export const isRoleAllowedInTWA = (role) => {
  return String(role || '').toUpperCase() === ALLOWED_ROLE;
};

/**
 * Reset del flag TWA (para debugging).
 */
export const resetTWAFlag = () => {
  try { window.localStorage.removeItem(STORAGE_KEY); } catch (_) { void 0; }
};

export const TWA_ALLOWED_ROLE = ALLOWED_ROLE;
